import { useEffect, useMemo, useRef, useState } from 'react';
import POIDetailModal from '@/components/modals/POIDetailModal';
import GameMapCanvas from '@/components/map/GameMapCanvas';
import PermissionsModal from '@/components/map/PermissionsModal';
import useGameStore from '@/store/gameStore';
import useGeolocation from '@/hooks/useGeolocation';
import * as mapService from '@/services/mapService';
import { resolveAssetUrl } from '@/utils/assets';

const PERMISSIONS_SEEN_KEY = 'hakken-permissions-seen';

const formatDistance = (meters) => {
  if (meters == null) return '—';
  return meters < 1000 ? `${Math.round(meters)} m` : `${(meters / 1000).toFixed(1)} km`;
};

const getPoiImage = (poi) => resolveAssetUrl(poi.imageUrl, '/branding/logo-hakkencity.png');

const getPoiState = (poi) => {
  if (poi.collectedRecently) return { label: 'Coletado hoje', className: 'bg-surfaceAlt text-fg/60' };
  if (poi.inRange) return { label: 'Ao alcance', className: 'bg-forest/20 text-forest-light' };
  return { label: 'Longe', className: 'bg-accent/20 text-accent' };
};

const toRad = (value) => (value * Math.PI) / 180;
const toDeg = (value) => (value * 180) / Math.PI;

const getBearing = (from, to) => {
  const lat1 = toRad(from.lat);
  const lat2 = toRad(to.lat);
  const deltaLng = toRad(to.lng - from.lng);
  const y = Math.sin(deltaLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(deltaLng);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
};

export default function MapExplorePage() {
  const [loading, setLoading] = useState(false);
  const [cityLoading, setCityLoading] = useState(false);
  const [selectedZone, setSelectedZone] = useState(null);
  const [movementHeading, setMovementHeading] = useState(null);
  const [showPermissions, setShowPermissions] = useState(false);
  const [geoPermission, setGeoPermission] = useState('prompt'); // 'prompt'|'granted'|'denied'
  const previousPositionRef = useRef(null);
  const recenterMapRef = useRef(null);
  const nearbyRequestRef = useRef(0);
  const cityRequestRef = useRef(0);
  const {
    lat, lng, accuracy, heading, headingSource, speed,
    compassPermission, requestCompassPermission,
    isLoading: geoLoading, isUsingFallback, error: geoError,
    requestCurrentPosition,
  } = useGeolocation();

  // Mostra modal de permissões na primeira visita ao mapa
  useEffect(() => {
    const seen = localStorage.getItem(PERMISSIONS_SEEN_KEY);
    if (!seen) setShowPermissions(true);
  }, []);

  // Rastreia permissão de geolocalização
  useEffect(() => {
    if (!isUsingFallback) {
      setGeoPermission('granted');
    } else if (geoError?.includes('recusada')) {
      setGeoPermission('denied');
    }
  }, [isUsingFallback, geoError]);

  // Auto-ativar bússola ao entrar (dispositivos que não requerem permissão explícita)
  useEffect(() => {
    if (compassPermission === 'prompt' || compassPermission === 'unsupported') return;
    requestCompassPermission();
  }, [compassPermission]);

  const {
    nearbyPOIs, setNearbyPOIs, selectPOI, setCurrentPosition,
    allCityPOIs, setAllCityPOIs, cityViewMode, toggleCityViewMode,
    currentCity, setCurrentCity,
  } = useGameStore();

  const sortedPOIs = useMemo(() => {
    const playerAccuracy = accuracy ?? 0;
    return [...nearbyPOIs]
      .map((p) => ({
        ...p,
        inRange: !p.collectedRecently && (p.distance ?? 9999) <= (p.collectionRadius || 100) + playerAccuracy,
      }))
      .sort((a, b) => {
        if (a.collectedRecently !== b.collectedRecently) return a.collectedRecently ? 1 : -1;
        if (a.inRange !== b.inRange) return a.inRange ? -1 : 1;
        return (a.distance || 999999) - (b.distance || 999999);
      });
  }, [nearbyPOIs, accuracy]);

  // Zonas únicas dos POIs da cidade para filtro
  const cityZones = useMemo(() => {
    const map = new Map();
    allCityPOIs.forEach((p) => {
      if (p.zone && !map.has(p.zone.id)) map.set(p.zone.id, p.zone);
    });
    return Array.from(map.values());
  }, [allCityPOIs]);

  const filteredCityPOIs = useMemo(() => {
    if (!selectedZone) return allCityPOIs;
    return allCityPOIs.filter((p) => p.zone?.id === selectedZone);
  }, [allCityPOIs, selectedZone]);

  const loadNearbyPOIs = async (position = { lat, lng }) => {
    if (!Number.isFinite(Number(position.lat)) || !Number.isFinite(Number(position.lng))) return;
    const requestId = ++nearbyRequestRef.current;
    setLoading(true);
    try {
      const res = await mapService.getNearbyPOIs(position.lat, position.lng, 2000);
      if (requestId !== nearbyRequestRef.current) return;
      setNearbyPOIs(res.data.data || []);
    } catch {}
    finally {
      if (requestId === nearbyRequestRef.current) setLoading(false);
    }
  };

  const loadCityPOIs = async (cityId, position = { lat, lng }) => {
    if (!cityId) return;
    const requestId = ++cityRequestRef.current;
    setCityLoading(true);
    try {
      const res = await mapService.getPOIsByCity(cityId, position.lat, position.lng);
      if (requestId !== cityRequestRef.current) return;
      setAllCityPOIs(res.data.data || []);
    } catch {}
    finally {
      if (requestId === cityRequestRef.current) setCityLoading(false);
    }
  };

  // Carrega cidade mais próxima uma vez
  useEffect(() => {
    if (geoLoading || currentCity) return;
    mapService.getCities().then((res) => {
      const cities = res.data.data || [];
      if (!cities.length) return;
      if (cities.length === 1) { setCurrentCity(cities[0]); return; }
      // Escolhe a mais próxima usando haversine simples
      const closest = cities.reduce((best, city) => {
        if (!city.lat || !city.lng) return best;
        const dLat = (lat - parseFloat(city.lat)) * (Math.PI / 180);
        const dLng = (lng - parseFloat(city.lng)) * (Math.PI / 180);
        const dist = dLat * dLat + dLng * dLng;
        return dist < best.dist ? { city, dist } : best;
      }, { city: cities[0], dist: Infinity });
      setCurrentCity(closest.city);
    }).catch(() => {});
  }, [geoLoading, lat, lng]);

  useEffect(() => {
    const previous = previousPositionRef.current;
    if (previous && lat && lng) {
      const movedEnough =
        Math.abs(previous.lat - lat) > 0.00001 ||
        Math.abs(previous.lng - lng) > 0.00001;
      if (movedEnough) setMovementHeading(getBearing(previous, { lat, lng }));
    }
    previousPositionRef.current = { lat, lng };
  }, [lat, lng]);

  const playerHeading = heading ?? movementHeading;

  useEffect(() => {
    setCurrentPosition({ lat, lng, accuracy, heading: playerHeading, headingSource, speed, compassPermission, isUsingFallback });
  }, [accuracy, compassPermission, geoLoading, headingSource, isUsingFallback, lat, lng, playerHeading, speed]);

  // Auto-centrar no jogador ao entrar no mapa
  useEffect(() => {
    if (!geoLoading) recenterMapRef.current?.();
  }, [geoLoading]);

  // Auto-centrar a cada 5 minutos
  useEffect(() => {
    if (geoLoading) return undefined;
    const interval = window.setInterval(() => recenterMapRef.current?.(), 5 * 60 * 1000);
    return () => window.clearInterval(interval);
  }, [geoLoading]);

  useEffect(() => {
    if (!geoLoading) loadNearbyPOIs({ lat, lng });
  }, [geoLoading, lat, lng]);

  useEffect(() => {
    if (geoLoading) return undefined;

    const refreshPOIs = () => {
      loadNearbyPOIs({ lat, lng });
      if (cityViewMode && currentCity?.id) loadCityPOIs(currentCity.id, { lat, lng });
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refreshPOIs();
    };

    window.addEventListener('focus', refreshPOIs);
    window.addEventListener('online', refreshPOIs);
    document.addEventListener('visibilitychange', handleVisibility);
    const interval = window.setInterval(refreshPOIs, 30000);

    return () => {
      window.removeEventListener('focus', refreshPOIs);
      window.removeEventListener('online', refreshPOIs);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.clearInterval(interval);
    };
  }, [cityViewMode, currentCity?.id, geoLoading, lat, lng]);

  const handleToggleCityView = async () => {
    const nextCityViewMode = !cityViewMode;
    toggleCityViewMode();
    setSelectedZone(null);
    if (nextCityViewMode && currentCity) {
      await loadCityPOIs(currentCity.id, { lat, lng });
    } else {
      await loadNearbyPOIs({ lat, lng });
    }
  };

  const closeCityView = async () => {
    if (!cityViewMode) return;
    toggleCityViewMode();
    setSelectedZone(null);
    await loadNearbyPOIs({ lat, lng });
  };

  return (
    <main className="relative h-full overflow-hidden bg-background">
      <GameMapCanvas
        pois={sortedPOIs}
        position={{ lat, lng, accuracy, heading: playerHeading, headingSource, speed, compassPermission, isUsingFallback }}
        onSelect={selectPOI}
        onRecenterRef={recenterMapRef}
        onRequestCompass={requestCompassPermission}
        cityViewMode={cityViewMode}
        allCityPOIs={filteredCityPOIs}
      />

      {/* Dica de jogo autoexplicativa — diz sempre ao jogador o próximo passo */}
      {!cityViewMode && (() => {
        const inRangePOI = sortedPOIs.find((p) => p.inRange);
        const nextPOI = inRangePOI || sortedPOIs.find((p) => !p.collectedRecently);
        if (loading && sortedPOIs.length === 0) {
          return (
            <div className="game-hint absolute left-3 top-3 z-20 animate-pop-in" style={{ right: 150 }}>
              <span className="text-base" aria-hidden>🔍</span>
              <span>À procura de tesouros perto de ti…</span>
            </div>
          );
        }
        if (!nextPOI) return null;
        return (
          <button
            type="button"
            onClick={() => selectPOI(nextPOI)}
            className="game-hint absolute left-3 top-3 z-20 animate-pop-in text-left active:scale-95"
            style={{ right: 150 }}
          >
            <span className="text-base" aria-hidden>{inRangePOI ? '🎉' : '🧭'}</span>
            <span className="min-w-0">
              {inRangePOI ? (
                <>Estás ao alcance! Toca em <span className="text-accent">{inRangePOI.name}</span> e coleta <span className="text-accent">+{inRangePOI.coinReward} moedas</span></>
              ) : (
                <>Segue até <span className="text-accent">{nextPOI.name}</span> ({formatDistance(nextPOI.distance)}) e ganha <span className="text-accent">+{nextPOI.coinReward} moedas</span></>
              )}
            </span>
          </button>
        );
      })()}

      {/* botões laterais */}
      {!cityViewMode && (
      <div className="absolute left-4 z-20 flex flex-col gap-3" style={{ bottom: 'var(--map-control-bottom)' }}>
        {/* Recentrar */}
        <button
          type="button"
          onClick={() => recenterMapRef.current?.()}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-edge bg-surface text-accent shadow-[0_2px_10px_rgba(0,0,0,0.5)] active:scale-95 transition-transform"
          aria-label="Recentrar mapa"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" className="h-5 w-5" aria-hidden="true">
            <circle cx="12" cy="12" r="3" fill="currentColor" stroke="none" />
            <line x1="12" y1="2" x2="12" y2="7" />
            <line x1="12" y1="17" x2="12" y2="22" />
            <line x1="2" y1="12" x2="7" y2="12" />
            <line x1="17" y1="12" x2="22" y2="12" />
          </svg>
        </button>

        {/* Ver cidade */}
        <button
          type="button"
          onClick={handleToggleCityView}
          disabled={cityLoading}
          className={`flex h-11 w-11 items-center justify-center rounded-full border shadow-[0_2px_10px_rgba(0,0,0,0.5)] active:scale-95 transition-transform ${
            cityViewMode
              ? 'border-accent bg-accent text-white shadow-[0_0_14px_rgba(232,83,29,0.5)]'
              : 'border-edge bg-surface text-accent'
          }`}
          aria-label={cityViewMode ? 'Voltar a POIs próximos' : 'Ver todos os POIs da cidade'}
        >
          {cityLoading ? (
            <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="h-5 w-5" aria-hidden="true">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
          )}
        </button>
      </div>
      )}

      {!cityViewMode && (
        <section className="absolute inset-x-0 z-10 overflow-x-auto" style={{ bottom: 'var(--map-card-bottom)' }}>
          <div className="flex gap-3 px-4 pb-1">
            {sortedPOIs.slice(0, 12).map((poi) => {
              const state = getPoiState(poi);
              return (
                <button
                  key={poi.id}
                  type="button"
                  onClick={() => selectPOI(poi)}
                  className="h-[96px] w-64 flex-shrink-0 rounded-2xl border border-edge bg-surface/95 p-2 text-left text-fg shadow-game-card"
                >
                  <div className="flex gap-3">
                    <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-xl border border-edge bg-surfaceAlt">
                      <img src={getPoiImage(poi)} alt={poi.name} className="h-full w-full object-cover" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2 className="truncate font-serif text-sm font-black">{poi.name}</h2>
                      <p className="mt-1 truncate text-xs font-bold text-fg/60">
                        {formatDistance(poi.distance)} · {poi.zone?.name || 'Zona por descobrir'}
                      </p>
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <span className="text-xs font-black text-accent">+{poi.coinReward} moedas</span>
                        <span className={`rounded-lg px-2 py-1 text-[10px] font-black ${state.className}`}>
                          {state.label}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Painel inferior — modo cidade: filtro de zonas + lista vertical */}
      {cityViewMode && (
        <button
          type="button"
          onClick={closeCityView}
          className="absolute inset-0 z-[9] cursor-default bg-transparent"
          aria-label="Fechar lista de POIs"
        />
      )}

      {cityViewMode && (
        <section
          className="absolute inset-x-0 z-10 flex flex-col"
          style={{ bottom: 0, maxHeight: '52%' }}
        >
          {/* Pills de zonas — fixas no topo, nunca encolhem */}
          <div className="flex-shrink-0 overflow-x-auto border-t border-edge bg-surface/95 px-4 py-2">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={closeCityView}
                className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-edge bg-surfaceAlt text-muted active:scale-95"
                aria-label="Fechar lista de POIs"
              >
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="h-3.5 w-3.5" aria-hidden="true">
                  <path d="M3 3l10 10M13 3 3 13" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => setSelectedZone(null)}
                className={`flex-shrink-0 rounded-full px-3 py-1 text-xs font-black transition-colors ${
                  selectedZone === null
                    ? 'bg-accent text-white'
                    : 'border border-edge text-muted'
                }`}
              >
                Todas
              </button>
              {cityZones.map((zone) => (
                <button
                  key={zone.id}
                  type="button"
                  onClick={() => setSelectedZone(selectedZone === zone.id ? null : zone.id)}
                  className={`flex-shrink-0 rounded-full px-3 py-1 text-xs font-black transition-colors ${
                    selectedZone === zone.id
                      ? 'text-fg'
                      : 'border border-edge text-muted'
                  }`}
                  style={selectedZone === zone.id ? { backgroundColor: zone.color || 'rgb(var(--hc-accent))' } : {}}
                >
                  {zone.name}
                </button>
              ))}
            </div>
          </div>

          {/* Lista de POIs — ocupa o espaço restante com scroll */}
          <div className="min-h-0 flex-1 overflow-y-auto bg-surface/95 px-4 pb-4">
            {filteredCityPOIs.length === 0 && (
              <p className="py-6 text-center text-sm text-muted">
                {cityLoading ? 'A carregar…' : 'Nenhum POI encontrado.'}
              </p>
            )}
            <div className="flex flex-col gap-2 pt-2">
              {filteredCityPOIs.map((poi) => {
                const state = getPoiState(poi);
                return (
                  <button
                    key={poi.id}
                    type="button"
                    onClick={() => selectPOI(poi)}
                    className="flex w-full gap-3 rounded-2xl border border-edge bg-surface/95 p-2 text-left text-fg shadow-game-card"
                  >
                    <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-xl border border-edge bg-surfaceAlt">
                      <img src={getPoiImage(poi)} alt={poi.name} className="h-full w-full object-cover" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2 className="truncate font-serif text-sm font-black">{poi.name}</h2>
                      <p className="mt-0.5 truncate text-xs font-bold text-fg/60">
                        {poi.zone?.name || 'Zona por descobrir'}
                        {poi.distance != null && ` · ${formatDistance(poi.distance)}`}
                      </p>
                      <div className="mt-1 flex items-center justify-between gap-2">
                        <span className="text-xs font-black text-accent">+{poi.coinReward} moedas</span>
                        <span className={`rounded-lg px-2 py-0.5 text-[10px] font-black ${state.className}`}>
                          {state.label}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <POIDetailModal />

      {showPermissions && (
        <PermissionsModal
          geoPermission={geoPermission}
          compassPermission={compassPermission}
          onRequestGeo={async () => {
            setGeoPermission('loading');
            await requestCurrentPosition();
          }}
          onRequestCompass={requestCompassPermission}
          onClose={() => setShowPermissions(false)}
        />
      )}
    </main>
  );
}
